import React from 'react';
import { Label } from '@patternfly/react-core';
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, StarIcon } from '@patternfly/react-icons';
import { formatPrice } from '../../../utils/money';
import { formatMonthYearNumeric } from '../../../utils/dateFormat';
import type { PaymentMethod } from '../../../types/models/PaymentMethod';
import type { WalletTransactionKind } from '../../../types/models/Wallet';

// Shared display helpers for the wallet / saved-card surfaces (Billing tab, Pay-invoice modal, the
// domain-order recap steps, the admin Wallets pages). Money is a 2-decimal STRING end-to-end; the
// parseFloat here is for display gating and formatting only — never to send a value back.

/** `"Visa •••• 4242"` — brand + masked last four. Falls back gracefully when Mollie gave no brand. */
export function describeCard(card: Pick<PaymentMethod, 'card_label' | 'card_last4'>): string {
  const brand = card.card_label ?? 'Card';
  return card.card_last4 ? `${brand} •••• ${card.card_last4}` : brand;
}

/** Card expiry as `MM/YYYY` (the backend sends the last day of the expiry month). */
export function formatCardExpiry(card: Pick<PaymentMethod, 'card_expiry'>): string {
  return formatMonthYearNumeric(card.card_expiry);
}

/** A card the backend will actually charge: `valid` and not past its expiry month. */
export function isUsableCard(card: Pick<PaymentMethod, 'status' | 'is_expired'>): boolean {
  return card.status === 'valid' && !card.is_expired;
}

/** Numeric view of a money string for display gating; `NaN` when unparseable. */
export function moneyToNumber(value: string): number {
  return parseFloat(value);
}

/** `true` when the balance string covers the amount string (display gating only — the backend re-checks). */
export function balanceCovers(balance: string, amount: string): boolean {
  const b = moneyToNumber(balance);
  const a = moneyToNumber(amount);
  return !Number.isNaN(b) && !Number.isNaN(a) && b >= a;
}

export function isNegativeMoney(value: string): boolean {
  const n = moneyToNumber(value);
  return !Number.isNaN(n) && n < 0;
}

/**
 * A signed ledger amount, coloured green for a credit and red for a debit, with an explicit `+` on
 * credits so the sign never relies on colour alone. `"-18.15"` → `-18,15 €` (red); `"100.00"` →
 * `+100,00 €` (green).
 */
export const SignedAmount: React.FunctionComponent<{ value: string }> = ({ value }) => {
  const n = moneyToNumber(value);
  if (Number.isNaN(n)) {
    return <>{value}</>;
  }
  const negative = n < 0;
  const color = negative
    ? 'var(--pf-t--global--text--color--status--danger--default)'
    : 'var(--pf-t--global--text--color--status--success--default)';
  return (
    <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
      {negative ? formatPrice(value) : `+${formatPrice(value)}`}
    </span>
  );
};

/** Balance figure — red when negative (a receivable after a Mollie refund/chargeback). */
export const BalanceAmount: React.FunctionComponent<{ value: string }> = ({ value }) => (
  <span
    style={
      isNegativeMoney(value)
        ? { color: 'var(--pf-t--global--text--color--status--danger--default)', fontVariantNumeric: 'tabular-nums' }
        : { fontVariantNumeric: 'tabular-nums' }
    }
  >
    {formatPrice(value)}
  </span>
);

const KIND_LABELS: Record<
  WalletTransactionKind,
  { text: string; color: 'green' | 'blue' | 'teal' | 'red' | 'purple' }
> = {
  deposit: { text: 'Top-up', color: 'green' },
  charge: { text: 'Charge', color: 'blue' },
  reversal: { text: 'Reversal', color: 'teal' },
  clawback: { text: 'Clawback', color: 'red' },
  adjustment: { text: 'Adjustment', color: 'purple' },
};

/** Human label for a ledger `kind`. Unknown kinds (a future vocabulary) render verbatim. */
export function transactionKindText(kind: string): string {
  return (KIND_LABELS as Record<string, { text: string } | undefined>)[kind]?.text ?? kind;
}

/**
 * The ledger row's kind as a chip. Pass the row's signed `amount`: a `clawback` is normally a debit (a
 * Mollie refund / chargeback, or a refund staff recorded before making it at Mollie), but a POSITIVE one
 * is staff cancelling such a recorded refund — shown « Refund cancelled » (teal). Presentation only: the
 * kind stays `clawback` (the accounting view skips wallet rows by kind), and `transactionKindText` stays
 * kind-level for the export's kind filter.
 */
export const TransactionKindLabel: React.FunctionComponent<{ kind: string; amount?: string }> = ({ kind, amount }) => {
  const isCancelledRefund = kind === 'clawback' && amount !== undefined && moneyToNumber(amount) > 0;
  const def = isCancelledRefund
    ? { text: 'Refund cancelled', color: 'teal' as const }
    : (
        KIND_LABELS as Record<string, { text: string; color: 'green' | 'blue' | 'teal' | 'red' | 'purple' } | undefined>
      )[kind];
  return (
    <Label isCompact color={def?.color ?? 'grey'}>
      {def?.text ?? kind}
    </Label>
  );
};

/**
 * Status chips for a saved card: "Default" (the card charges use when none is named), "Expired"
 * (past its expiry month — still `valid` at Mollie but will decline) and "Invalid" (Mollie says so).
 * Each chip carries an icon so status never relies on colour alone.
 */
export const CardStatusLabels: React.FunctionComponent<{ card: PaymentMethod }> = ({ card }) => (
  <>
    {card.is_default && (
      <Label isCompact color="blue" icon={<StarIcon />}>
        Default
      </Label>
    )}
    {card.status === 'valid' && !card.is_expired && !card.is_default && (
      <Label isCompact color="green" icon={<CheckCircleIcon />}>
        Valid
      </Label>
    )}
    {card.is_expired && (
      <Label isCompact color="orange" icon={<ExclamationTriangleIcon />}>
        Expired
      </Label>
    )}
    {card.status === 'invalid' && (
      <Label isCompact color="red" icon={<ExclamationCircleIcon />}>
        Invalid
      </Label>
    )}
    {card.status === 'pending' && (
      <Label isCompact color="grey">
        Pending
      </Label>
    )}
  </>
);

/** Copy shown wherever a money-moving control is hidden because a staff impersonation session is active. */
export const IMPERSONATION_MONEY_NOTICE =
  'You are viewing this organization through an impersonation session. Paying from its prepaid balance, charging or managing its saved cards is not available — only a hosted checkout, where the card holder pays, can be started.';
